import React, { useCallback, useEffect, useState } from 'react';

import { Box, Stack } from '@chakra-ui/react';
import { Application } from 'pixi.js';

import { formatTooltip, renderTooltipFieldValue } from 'components/DataDisplay';
import { AGraphEdge } from 'components/Visualization/PixiGraph/core/Edge';
import { AGraph } from 'components/Visualization/PixiGraph/core/Graph';
import { AGraphNode } from 'components/Visualization/PixiGraph/core/Node';
import { AGraphRenderer } from 'components/Visualization/PixiGraph/core/Renderer';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { highlightEdges } from 'containers/Visibility/SecurityGraphNext/utils/highlight_edges';

import { getActivityActionLabel, getActivityActions } from './utils';

interface ActivityGraphMenuProps {
  app: Application;
  graph: AGraph<AGraphNode, AGraphEdge>;
  renderer: AGraphRenderer;
}

const isPlaceholderNode = (node?: AGraphNode) =>
  node?.data?.is_placeholder === true;

const getNodeFromEntries = (entries: Array<{ data?: unknown }> = []) =>
  entries
    .map(entry => entry.data)
    .map(data =>
      data instanceof AGraphNode ? data : (data as { node?: AGraphNode })?.node,
    )
    .find((node): node is AGraphNode => node instanceof AGraphNode);

export const ActivityGraphMenu = ({
  app,
  graph,
  renderer,
}: ActivityGraphMenuProps) => {
  const [hoveredNode, setHoveredNode] = useState<AGraphNode>();
  const [hoveredActionsNode, setHoveredActionsNode] = useState<AGraphNode>();
  const [highlightedEdgeId, setHighlightedEdgeId] = useState<string>();
  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();
  const { cloudAccountIDMapping } = useCloudAccountId();

  const getNodePosition = useCallback(
    (node: AGraphNode) => {
      const scale = app.stage.scale.x;
      return {
        left: app.stage.x + (node.x + node.w) * scale + 10,
        top: app.stage.y + node.y * scale,
      };
    },
    [app.stage],
  );

  const openNodeMetadata = useCallback(
    (node?: AGraphNode) => {
      if (!node) return;

      const data = node.data;
      updateActiveResource({
        resourceType: data.native_name,
        accountId:
          cloudAccountIDMapping?.[data.account]?.uuid ??
          data.account ??
          undefined,
        // Activity graph `resource_id` may be a UUID or folder identity.
        // The API's `arn` field is the only value that belongs in arns[].
        resourceArn: data.arn,
        cloudProvider: data.provider,
      });
      onOpenMetadata();
    },
    [cloudAccountIDMapping, onOpenMetadata, updateActiveResource],
  );

  useEffect(() => {
    const handleNodeClick = entries => {
      if (
        entries.some(entry => entry.data?.isActivityPermissionsIcon === true)
      ) {
        return;
      }

      const edgeEntry = entries.find(entry => entry.data instanceof AGraphEdge);
      if (edgeEntry) {
        if (highlightedEdgeId === edgeEntry.data.id) {
          setHighlightedEdgeId(undefined);
          return;
        }

        if (!edgeEntry.id.includes('edge:path:')) {
          highlightEdges(edgeEntry, renderer, app, graph);
        }
        edgeEntry.data.renderActive(renderer.activeGfx);
        setHighlightedEdgeId(edgeEntry.data.id);
        return;
      }

      const node = getNodeFromEntries(entries);
      setHighlightedEdgeId(undefined);
      if (!isPlaceholderNode(node)) openNodeMetadata(node);
    };
    const showTooltip = entries => {
      const permissionsIconEntry = entries.find(
        entry => entry.data?.isActivityPermissionsIcon === true,
      );
      if (permissionsIconEntry) {
        setHoveredNode(undefined);
        setHoveredActionsNode(permissionsIconEntry.data.node);
        return;
      }

      const edgeEntry = entries.find(entry => entry.data instanceof AGraphEdge);
      if (edgeEntry) {
        setHoveredNode(undefined);
        setHoveredActionsNode(undefined);
        return;
      }

      const node = getNodeFromEntries(entries);
      setHoveredNode(node);
      setHoveredActionsNode(undefined);
    };
    const hideTooltip = () => {
      setHoveredNode(undefined);
      setHoveredActionsNode(undefined);
    };
    const hideOverlays = () => {
      setHoveredNode(undefined);
      setHoveredActionsNode(undefined);
    };

    graph.on('mousedown:element', handleNodeClick);
    graph.on('mouseover:element', showTooltip);
    graph.on('mouseout:element', hideTooltip);
    graph.on('wheel', hideOverlays);

    return () => {
      graph.off('mousedown:element', handleNodeClick);
      graph.off('mouseover:element', showTooltip);
      graph.off('mouseout:element', hideTooltip);
      graph.off('wheel', hideOverlays);
    };
  }, [app, graph, highlightedEdgeId, openNodeMetadata, renderer]);

  return (
    <>
      {hoveredNode && (
        <Box
          pos="absolute"
          bg="#2D3748"
          borderRadius={4}
          color="white"
          fontSize="sm"
          px={3}
          py={2}
          w="280px"
          zIndex={2}
          pointerEvents="none"
          {...getNodePosition(hoveredNode)}
        >
          {formatTooltip({
            header: 'Overview',
            width: 'full',
            content: (
              <Stack spacing={1}>
                {renderTooltipFieldValue(
                  'Name',
                  hoveredNode.data.name ?? hoveredNode.data.resource_id ?? '-',
                )}
                {renderTooltipFieldValue(
                  'Type',
                  hoveredNode.data.native_display_name ??
                    hoveredNode.data.ariksaType?.resource_alias ??
                    hoveredNode.data.ariksaType?.cloud_native_name ??
                    hoveredNode.data.native_name ??
                    '-',
                )}
              </Stack>
            ),
          })}
        </Box>
      )}
      {hoveredActionsNode && (
        <Box
          pos="absolute"
          bg="#2D3748"
          borderRadius={4}
          color="white"
          fontSize="sm"
          px={3}
          py={2}
          w="280px"
          zIndex={2}
          pointerEvents="none"
          {...getNodePosition(hoveredActionsNode)}
        >
          {formatTooltip({
            header: 'Actions Performed',
            width: 'full',
            content: (
              <Stack spacing={0}>
                {getActivityActions(hoveredActionsNode.data?.actions).map(
                  (action, index) => (
                    <Box key={`${getActivityActionLabel(action)}-${index}`}>
                      {index + 1}. {getActivityActionLabel(action)}
                    </Box>
                  ),
                )}
              </Stack>
            ),
          })}
        </Box>
      )}
    </>
  );
};
